import { create } from "zustand";
import { fetchUserCreditBalance } from "apis/creditService";

// Shared credit balance so any part of the app (e.g. the request-update flow)
// can optimistically deduct credits and the Sidebar reflects it immediately,
// then reconcile with the server in the background.
interface CreditState {
  availableCredits: number | null;
  loading: boolean;
  refresh: () => Promise<void>;
  decrement: (by?: number) => void;
}

// De-dupes concurrent refreshes: multiple components (Sidebar, CreditsButton,
// the request-update flow) and React StrictMode's double-mount all share one
// in-flight network request instead of firing several identical calls.
let inFlight: Promise<void> | null = null;

export const useCreditStore = create<CreditState>((set) => ({
  availableCredits: null,
  loading: false,
  refresh: () => {
    if (inFlight) return inFlight;
    set({ loading: true });
    inFlight = (async () => {
      try {
        const res = await fetchUserCreditBalance();
        set({ availableCredits: Number(res.availableCredits), loading: false });
      } catch (err) {
        console.error("Failed to fetch credit balance:", err);
        set({ loading: false });
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  },
  decrement: (by = 1) =>
    set((state) => ({
      availableCredits:
        state.availableCredits == null
          ? state.availableCredits
          : Math.max(0, state.availableCredits - by),
    })),
}));
